// src/pages/AdminOrganizers.js
//
// App Settings → Organizers sub-tab. Platform-admin-only.
//
// Lists all organizers with a color-coded status badge, a pending_review
// approval queue at the top, tournament counts, and a status control.
// The "Impersonate" button is present but disabled until the impersonation
// feature (session-swap + audit trail) ships -- it's listed separately on
// the roadmap from this dashboard.

import React, { useEffect, useState, useCallback } from 'react';
import { getOrganizers, updateOrganizerStatus, impersonateOrganizer, assignOrganizerPlan } from '../services/api';

const STATUS_BADGE = {
    active:          'bg-green-900 text-green-400',
    suspended:       'bg-red-900 text-red-400',
    pending_review:  'bg-yellow-900 text-yellow-400',
};

const STATUS_LABEL = {
    active:         'Active',
    suspended:      'Suspended',
    pending_review: 'Pending Review',
};

// tos_status comes from GET /admin/organizers (src/routes/admin.js),
// computed by comparing each organizer's stored tos_version against the
// live combined ToS/Privacy version. 'outdated' is the one that matters
// operationally -- it means attachOrganizer is silently locking that
// organizer out of admin actions right now, with no other indication of
// why (this was previously invisible entirely -- see the July 2026
// production incident that motivated adding this column). 'current' is
// intentionally understated (no loud badge) since it's the expected,
// unremarkable state; 'never_accepted' is neutral/gray since it's the
// normal state for an organizer who hasn't completed onboarding yet, not
// itself a problem.
const TOS_BADGE = {
    current:         'bg-gray-800 text-gray-500',
    never_accepted:  'bg-gray-800 text-gray-500',
    outdated:        'bg-red-900 text-red-400',
};

const TOS_LABEL = {
    current:        '✓ Current',
    never_accepted: 'Not yet accepted',
    outdated:       '⚠ Outdated',
};

const PLAN_LABEL = {
    eval:  'Eval',
    tier1: 'Starter ($29)',
    tier2: 'Growth ($49)',
    tier3: 'Unlimited ($100)',
};

function AdminOrganizers({ user = {}, onUserChange, showFeedback }) {
    // Deliberately checks is_platform_admin ONLY, not is_admin. The backend's
    // platformAdminOnly middleware (which actually protects these endpoints)
    // checks is_platform_admin exclusively -- is_admin is a legacy flag from
    // before organizers/platform_admin existed, and someone with only that
    // legacy flag set is NOT a platform admin as far as this API is
    // concerned. Using `|| user.is_admin` here let the fetch be attempted
    // for such a user, the backend correctly 403'd it, and the resulting
    // error toast fed back into a re-render loop (see Admin.js showFeedback).
    const isPlatformAdmin = user.is_platform_admin;
    const currentParticipantId = user.id;

    const [organizers, setOrganizers] = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [editingId,  setEditingId]  = useState(null);

    const fetchOrganizers = useCallback(() => {
        if (!isPlatformAdmin) return;
        setLoading(true);
        getOrganizers()
            .then(res => setOrganizers(res.data))
            .catch(() => showFeedback('Failed to load organizers', 'error'))
            .finally(() => setLoading(false));
    }, [isPlatformAdmin, showFeedback]);

    useEffect(() => { fetchOrganizers(); }, [fetchOrganizers]);

    const handleStatusChange = async (organizer, newStatus) => {
        setEditingId(null);
        if (newStatus === organizer.status) return;
        try {
            await updateOrganizerStatus(organizer.id, newStatus);
            showFeedback(`${organizer.display_name} is now ${STATUS_LABEL[newStatus].toLowerCase()}`, 'success');
            fetchOrganizers();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to update status', 'error');
        }
    };

    const [impersonatingId, setImpersonatingId] = useState(null);
    const [editingPlanId, setEditingPlanId] = useState(null);

    const handlePlanChange = async (organizer, planId) => {
        setEditingPlanId(null);
        if (!planId) return;
        try {
            await assignOrganizerPlan(organizer.id, planId, true);
            showFeedback(`${organizer.display_name} assigned to ${PLAN_LABEL[planId]} and marked paid`, 'success');
            fetchOrganizers();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to assign plan', 'error');
        }
    };

    const handleImpersonate = async (organizer) => {
        setImpersonatingId(organizer.id);
        try {
            const res = await impersonateOrganizer(organizer.id);
            onUserChange(res.data);
            showFeedback(`Now viewing as ${organizer.display_name}`, 'success');
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to start impersonation', 'error');
        } finally {
            setImpersonatingId(null);
        }
    };

    if (!isPlatformAdmin) {
        return (
            <div className="max-w-lg">
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 text-center">
                    <p className="text-gray-400 text-sm">
                        Organizer management is only available to platform administrators.
                    </p>
                </div>
            </div>
        );
    }

    const pendingCount = organizers.filter(o => o.status === 'pending_review').length;

    return (
        <div className="max-w-5xl">
            <div className="flex items-center justify-between mb-1">
                <h3 className="text-white font-bold text-lg">Organizers</h3>
                {pendingCount > 0 && (
                    <span className="bg-yellow-900 text-yellow-400 text-xs font-bold px-2.5 py-1 rounded-full">
                        {pendingCount} pending review
                    </span>
                )}
            </div>
            <p className="text-gray-500 text-sm mb-6">
                Approve new organizers, suspend accounts, and review tournament ownership.
            </p>

            {loading ? (
                <div className="bg-gray-900 border border-gray-800 rounded-xl px-4 py-6 text-gray-500 text-sm text-center">
                    Loading…
                </div>
            ) : organizers.length === 0 ? (
                <div className="bg-gray-900 border border-gray-800 rounded-xl px-4 py-6 text-gray-500 text-sm text-center">
                    No organizers found.
                </div>
            ) : (
                <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-800">
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Organizer</th>
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Status</th>
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">ToS</th>
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Plan</th>
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Slug</th>
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Usage</th>
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {organizers.map((o, i) => {
                                const isSelf = o.participant_id === currentParticipantId;
                                return (
                                    <tr key={o.id} className={'border-b border-gray-800 ' + (i % 2 === 0 ? '' : 'bg-gray-900/50')}>
                                        <td className="px-4 py-3 text-white font-medium">
                                            <div>
                                                {o.display_name}
                                                {o.is_platform_admin && (
                                                    <span className="ml-2 text-xs text-orange-400">(platform admin)</span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500 font-normal">{o.participant_name} · {o.participant_email}</div>
                                            <div className="text-xs text-gray-600 font-normal">
                                                Since {new Date(o.created_at).toLocaleDateString('en-US', {
                                                    month: 'short', day: 'numeric', year: 'numeric',
                                                })}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {editingId === o.id ? (
                                                <select
                                                    autoFocus
                                                    defaultValue={o.status}
                                                    onBlur={e => handleStatusChange(o, e.target.value)}
                                                    onChange={e => handleStatusChange(o, e.target.value)}
                                                    className="bg-gray-800 border border-orange-500 text-white text-xs rounded px-2 py-1 focus:outline-none">
                                                    <option value="active">Active</option>
                                                    <option value="suspended">Suspended</option>
                                                    <option value="pending_review">Pending Review</option>
                                                </select>
                                            ) : (
                                                <button
                                                    onClick={() => !isSelf && setEditingId(o.id)}
                                                    title={isSelf ? 'You cannot change your own status' : 'Click to change status'}
                                                    className={'text-xs px-2 py-0.5 rounded-full ' +
                                                        (isSelf ? 'cursor-default ' : 'cursor-pointer hover:ring-1 hover:ring-orange-500 ') +
                                                        STATUS_BADGE[o.status]}>
                                                    {STATUS_LABEL[o.status] || o.status}
                                                </button>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                title={
                                                    o.tos_status === 'outdated'
                                                        ? 'Stored ToS/Privacy version does not match the current published version -- this organizer is locked out of admin actions until they re-accept.'
                                                        : o.tos_status === 'never_accepted'
                                                        ? 'This organizer has not yet accepted the current ToS/Privacy Policy.'
                                                        : 'Accepted the current ToS/Privacy Policy version.'
                                                }
                                                className={'text-xs px-2 py-0.5 rounded-full ' + (TOS_BADGE[o.tos_status] || TOS_BADGE.never_accepted)}>
                                                {TOS_LABEL[o.tos_status] || o.tos_status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            {editingPlanId === o.id ? (
                                                <select
                                                    autoFocus
                                                    defaultValue={o.plan_id || ''}
                                                    onBlur={e => handlePlanChange(o, e.target.value)}
                                                    onChange={e => handlePlanChange(o, e.target.value)}
                                                    className="bg-gray-800 border border-orange-500 text-white text-xs rounded px-2 py-1 focus:outline-none">
                                                    <option value="" disabled>Choose a plan…</option>
                                                    <option value="eval">Eval</option>
                                                    <option value="tier1">Starter ($29)</option>
                                                    <option value="tier2">Growth ($49)</option>
                                                    <option value="tier3">Unlimited ($100)</option>
                                                </select>
                                            ) : (
                                                <button
                                                    onClick={() => setEditingPlanId(o.id)}
                                                    title="Click to assign a plan (marks paid immediately)"
                                                    className="text-xs px-2 py-0.5 rounded-full cursor-pointer hover:ring-1 hover:ring-orange-500 bg-gray-800 text-gray-300">
                                                    {o.plan_id
                                                        ? `${PLAN_LABEL[o.plan_id] || o.plan_id}${o.license_paid_at ? '' : ' (unpaid)'}`
                                                        : 'No plan'}
                                                </button>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-gray-400 font-mono text-xs">
                                            {o.slug ? `${o.slug}.heberthoops.com` : <span className="text-gray-600">Not set</span>}
                                        </td>
                                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                                            {o.tournament_count} tournament{o.tournament_count === 1 ? '' : 's'} · {o.pool_count} pool{o.pool_count === 1 ? '' : 's'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                onClick={() => !isSelf && handleImpersonate(o)}
                                                disabled={isSelf || impersonatingId === o.id}
                                                title={isSelf ? 'You cannot impersonate yourself' : `View the app as ${o.display_name}`}
                                                className={'text-xs px-2.5 py-1 rounded-lg transition-colors ' +
                                                    (isSelf || impersonatingId === o.id
                                                        ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                                                        : 'bg-gray-800 text-orange-400 hover:bg-gray-700 cursor-pointer')}>
                                                {impersonatingId === o.id ? 'Switching…' : 'Impersonate'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default AdminOrganizers;